import { useEffect, useState } from 'react';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ExternalLinkIcon, TriangleAlertIcon } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { type Translate } from '@shared/hooks/locale';

import {
  type CampaignValidationError,
  getCampaignRecipientProfilePath,
} from './campaignValidation';

type Props = {
  error: CampaignValidationError;
  loading: boolean;
  onEditEmail: () => void;
  onRefresh: () => void;
  onSendAnyway: () => void;
  t: Translate;
};

const RECIPIENTS_PAGE_SIZE = 50;

const getFieldLabel = (field: string, t: Props['t']): string => {
  switch (field) {
    case '#recipient.firstName':
      return t('First name');
    case '#recipient.lastName':
      return t('Last name');
    case '#recipient.title':
      return t('Title');
    case '#recipient.primaryPhoneNumber':
      return t('Phone number');
    case '#recipient.primaryEmail':
      return t('Primary email');
    case '#recipient.organisationName':
      return t('Organisation name');
    case '#recipient.organisationAddress':
      return t('Address');
    default:
      return field;
  }
};

export const CampaignValidationDialog = ({
  error,
  loading,
  onEditEmail,
  onRefresh,
  onSendAnyway,
  t,
}: Props) => {
  const [visibleRecipientCount, setVisibleRecipientCount] =
    useState(RECIPIENTS_PAGE_SIZE);

  useEffect(() => {
    setVisibleRecipientCount(RECIPIENTS_PAGE_SIZE);
  }, [error]);

  const missingFields = Array.from(
    new Set(
      error.invalidRecipients.flatMap(recipient => recipient.missingFields),
    ),
  );
  const hasMissingRecipientData = error.invalidRecipients.length > 0;
  const canSendAnyway =
    hasMissingRecipientData && error.invalidFields.length === 0;
  const visibleRecipients = error.invalidRecipients.slice(
    0,
    visibleRecipientCount,
  );
  const remainingRecipientCount =
    error.invalidRecipients.length - visibleRecipients.length;

  return (
    <Dialog
      aria-labelledby="campaign-validation-dialog-title"
      fullWidth
      maxWidth="md"
      onClose={onEditEmail}
      open
    >
      <DialogTitle id="campaign-validation-dialog-title" sx={{ pb: 1.5 }}>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: theme => alpha(theme.palette.warning.main, 0.12),
              borderRadius: '50%',
              color: 'warning.main',
              display: 'flex',
              flexShrink: 0,
              height: 40,
              justifyContent: 'center',
              width: 40,
            }}
          >
            <TriangleAlertIcon />
          </Box>
          <Stack spacing={0.25}>
            <Typography component="span" sx={{ fontWeight: 600 }} variant="h6">
              {hasMissingRecipientData
                ? t('Resolve missing recipient data')
                : t(
                    'This email contains a recipient placeholder that cannot be used.',
                  )}
            </Typography>
            {hasMissingRecipientData && (
              <Typography color="text.secondary" variant="body2">
                {t('contactsSelectedCount', {
                  count: error.invalidRecipients.length,
                })}
              </Typography>
            )}
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Stack spacing={2.5} sx={{ p: 3 }}>
          <Alert severity="warning" variant="outlined">
            <AlertTitle>{t('Nothing has been sent yet.')}</AlertTitle>
            {t('The entire batch is paused until you choose how to continue.')}
          </Alert>

          {missingFields.length > 0 && (
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                {t('Missing information')}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {missingFields.map(field => (
                  <Chip
                    key={field}
                    label={getFieldLabel(field, t)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </Stack>
          )}

          {error.invalidFields.length > 0 && (
            <Stack spacing={1}>
              <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                {t('Placeholders to edit')}
              </Typography>
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {error.invalidFields.map(field => (
                  <Chip key={field} label={field} size="small" color="error" />
                ))}
              </Stack>
            </Stack>
          )}

          {hasMissingRecipientData && (
            <Box
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Stack
                component="ul"
                role="list"
                sx={{
                  m: 0,
                  maxHeight: 280,
                  overflowY: 'auto',
                  p: 0,
                  '& > li + li': { borderColor: 'divider', borderTop: 1 },
                }}
              >
                {visibleRecipients.map(recipient => {
                  const label = recipient.name?.trim() || recipient.email;

                  return (
                    <Box
                      component="li"
                      key={`${recipient.userId ?? recipient.email}-${
                        recipient.email
                      }`}
                      sx={{
                        alignItems: 'center',
                        display: 'grid',
                        gap: 1.5,
                        gridTemplateColumns: {
                          xs: 'minmax(0, 1fr)',
                          sm: 'minmax(180px, 280px) minmax(0, 1fr)',
                        },
                        listStyle: 'none',
                        px: 2,
                        py: 1.25,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {recipient.userId ? (
                        <Link
                          component={RouterLink}
                          rel="noopener noreferrer"
                          sx={{
                            alignItems: 'center',
                            color: 'text.primary',
                            display: 'inline-flex',
                            fontWeight: 600,
                            gap: 0.5,
                            justifySelf: 'start',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                          target="_blank"
                          to={getCampaignRecipientProfilePath(recipient.userId)}
                          variant="body2"
                        >
                          {label}
                          <ExternalLinkIcon
                            color="var(--mui-palette-text-secondary)"
                            size={14}
                          />
                        </Link>
                      ) : (
                        <Typography sx={{ fontWeight: 600 }} variant="body2">
                          {label}
                        </Typography>
                      )}

                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.75,
                        }}
                      >
                        <Typography color="text.secondary" variant="caption">
                          {t('Missing')}:
                        </Typography>
                        {recipient.missingFields.map(field => (
                          <Chip
                            key={field}
                            label={getFieldLabel(field, t)}
                            size="small"
                            sx={{ height: 24 }}
                            variant="outlined"
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
                {remainingRecipientCount > 0 && (
                  <Box
                    component="li"
                    sx={{
                      listStyle: 'none',
                      px: 2,
                      py: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Button
                      onClick={() =>
                        setVisibleRecipientCount(count =>
                          Math.min(
                            count + RECIPIENTS_PAGE_SIZE,
                            error.invalidRecipients.length,
                          ),
                        )
                      }
                      size="small"
                    >
                      {t('+{{count}} more', {
                        count: Math.min(
                          remainingRecipientCount,
                          RECIPIENTS_PAGE_SIZE,
                        ),
                      })}
                    </Button>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          <Stack
            divider={<Divider flexItem />}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 2, px: 2 }}
          >
            {hasMissingRecipientData && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: { sm: 'center' }, py: 2 }}
              >
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                    {t('Fix the data, then refresh')}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {t(
                      'Update the missing information in the affected profiles, then refresh to check the data again.',
                    )}
                  </Typography>
                </Stack>
                <Button
                  loading={loading}
                  onClick={onRefresh}
                  variant="outlined"
                >
                  {t('Refresh')}
                </Button>
              </Stack>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ alignItems: { sm: 'center' }, py: 2 }}
            >
              <Stack spacing={0.25} sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                  {t('Edit the email')}
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {t(
                    'Remove or rewrite the dynamic field that requires this information.',
                  )}
                </Typography>
              </Stack>
              <Button disabled={loading} onClick={onEditEmail}>
                {t('editEmail')}
              </Button>
            </Stack>

            {canSendAnyway && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: { sm: 'center' }, py: 2 }}
              >
                <Stack spacing={0.25} sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600 }} variant="subtitle2">
                    {t('Ignore and send anyway')}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {t(
                      'Missing values will be replaced with blank text. All recipients will receive the email.',
                    )}
                  </Typography>
                </Stack>
                <Button
                  color="warning"
                  loading={loading}
                  onClick={onSendAnyway}
                  variant="contained"
                >
                  {t('Send anyway')}
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
